import type { TFunction } from "i18next";
import { isApiError } from "../../api/client.js";
import {
  outboundMediaValidationErrorKey,
  type OutboundMediaValidationErrorCode,
} from "./outbound-media-validation.js";

export function mapOutboundMediaUploadError(t: TFunction, error: unknown): string {
  if (isApiError(error)) {
    switch (error.code) {
      case "OUTBOUND_MEDIA_UNSUPPORTED_TYPE":
        return t("inbox.composer.attachmentErrors.unsupportedType");
      case "OUTBOUND_MEDIA_TOO_LARGE":
        return t("inbox.composer.attachmentErrors.tooLarge");
      case "OUTBOUND_MEDIA_EMPTY":
        return t("inbox.composer.attachmentErrors.empty");
      case "OUTBOUND_MEDIA_INVALID_FILENAME":
        return t("inbox.composer.attachmentErrors.invalidFilename");
      case "STORAGE_QUOTA_EXCEEDED":
        return t("inbox.composer.attachmentErrors.quota");
      case "PLATFORM_NOT_ENABLED":
        return t("inbox.composer.platformDisabled");
      default:
        if (error.status === 0) {
          return t("inbox.unreachable");
        }
        return mapOutboundSendError(t, error, t("inbox.composer.attachmentErrors.uploadFailed"));
    }
  }
  return t("inbox.composer.attachmentErrors.uploadFailed");
}

export function mapOutboundSendError(t: TFunction, error: unknown, fallback: string): string {
  if (!isApiError(error)) {
    return fallback;
  }
  if (error.status === 0) {
    return t("inbox.unreachable");
  }

  const message = error.message.toLowerCase();

  switch (error.code) {
    case "OUTBOUND_MEDIA_UNSUPPORTED_TYPE":
      return t("inbox.composer.sendErrors.unsupportedMedia");
    case "OUTBOUND_MEDIA_TOO_LARGE":
      return t("inbox.composer.sendErrors.tooLarge");
    case "OUTBOUND_MEDIA_EMPTY":
      return t("inbox.composer.sendErrors.empty");
    case "OUTBOUND_MEDIA_INVALID_FILENAME":
      return t("inbox.composer.sendErrors.invalidFilename");
    case "PLATFORM_NOT_ENABLED":
      return t("inbox.composer.sendErrors.platformDisabled");
    case "ORGANIZATION_HAS_NO_ACTIVE_OWNER":
      return t("organization.platformAdmin.noActiveOwner");
    case "ORGANIZATION_SUSPENDED":
      return t("organization.suspended.outboundBlocked");
    case "INSUFFICIENT_MESSAGE_CREDITS":
      return t("inbox.templateSend.errors.insufficientCredits");
    case "VALIDATION_ERROR":
      if (message.includes("attachment") && message.includes("not available")) {
        return t("inbox.composer.sendErrors.attachmentUnavailable");
      }
      if (message.includes("cannot be sent on this channel")) {
        return t("inbox.composer.sendErrors.unsupportedMedia");
      }
      if (message.includes("captions are not supported")) {
        return t("inbox.composer.sendErrors.captionUnsupported");
      }
      break;
    default:
      break;
  }

  if (message.includes("cannot be sent on this channel")) {
    return t("inbox.composer.sendErrors.unsupportedMedia");
  }

  return fallback;
}

export function mapOutboundMediaValidationError(
  t: TFunction,
  code: OutboundMediaValidationErrorCode,
): string {
  return t(outboundMediaValidationErrorKey(code));
}
